import { useQuestionaireRoute } from '@app/hooks/useQuestionaireRoute';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useTags } from '@app/hooks/useTags';
import Autocomplete from '@components/ui/Autocomplete/Autocomplete';
import { isSuperExpertHiddenQuestion } from '@app/constants/superExpertMode';
import { isSnippetCategoryKey, SERVICE_SELECTION_APP_ID } from '@app/constants/snippetCategoryKeys';
import { CompactServiceSelect } from '../Input/CompactServiceSelect';
import { Markdown } from '@components/ui/Markdown/Markdown';
import AllowedValueTypes from '@legalosApi/types/AllowedValueTypes';
import { IQuestion } from '@legalosApi/types/IQuestion';
import { ISelectOption } from '@legalosApi/types/ISelectOption';
import ISimpleDateObject from '@legalosApi/types/ISimpleDateObject';
import { QuestionnaireState } from '@legalosApi/types/QuestionnaireState';
import QuestionType from '@legalosApi/types/QuestionType';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Collapse } from '@mui/material';
import cx from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { UpdateQuestionCallback } from '../Quest';
import { AnyStep, ChoiceStep, MessageStep, QuestionStep } from '../utils';

export interface CompactStepProps {
  step: AnyStep;
  onUpdateQuestion: UpdateQuestionCallback;
  isNew?: boolean;
  /** Pre-fill value from SEMIAUTO user values resolution */
  prefillValue?: AllowedValueTypes;
  /** Application id of the enclosing questionnaire (drives service-select rendering). */
  applicationId?: string;
}

/**
 * CompactStep renders a single question as a compact form row for super expert mode.
 * Instead of the full-page layout, each question is shown inline with its input.
 */
export function CompactStep({ step, onUpdateQuestion, isNew, prefillValue, applicationId }: CompactStepProps) {
  const { jwt } = useQuestionaireRoute();

  const question: IQuestion | undefined = (step as QuestionStep | ChoiceStep | MessageStep).question;
  if (!question) return null;

  const isComplete = question.state === QuestionnaireState.COMPLETE;
  const isInfo =
    question.tags.process.includes('INFO') || question.options?.some((o) => o.origLabel.startsWith('Knoten'));
  const isUpload = question.tags.process.includes('UPLOAD');

  // INFO steps (and legacy Knoten questions) without UPLOAD are hidden (auto-answered by SuperExpertStream)
  if (isInfo && !isUpload) return null;

  // Hard-hidden system questions (DIETER_SUBSCRIPTION, COHORT, IS_WHITELABEL) are never rendered
  if (isSuperExpertHiddenQuestion(question)) return null;

  // INFO + UPLOAD: show as collapsible with file upload
  if (isInfo && isUpload) {
    return <CompactUploadStep step={step as MessageStep} onUpdateQuestion={onUpdateQuestion} />;
  }

  return (
    <CompactQuestionRow
      question={question}
      isComplete={isComplete}
      isNew={isNew}
      onUpdateQuestion={onUpdateQuestion}
      jwt={jwt}
      prefillValue={prefillValue}
      applicationId={applicationId}
    />
  );
}

/**
 * Renders a file upload step in compact mode
 */
function CompactUploadStep({
  step,
  onUpdateQuestion,
}: {
  step: MessageStep;
  onUpdateQuestion: UpdateQuestionCallback;
}) {
  const [expanded, setExpanded] = useState(false);
  const { question } = step;

  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
      <button
        className="flex items-center gap-2 w-full text-left text-sm font-medium text-slate-700"
        onClick={() => setExpanded(!expanded)}
      >
        <ExpandMoreIcon className={cx('transition-transform', { 'rotate-180': expanded })} fontSize="small" />
        <Markdown className="inline text-sm [&_p]:inline" text={question.questionText} />
      </button>
      <Collapse in={expanded}>
        <div className="mt-3">
          {question.description && <Markdown className="text-sm text-slate-600 mb-3" text={question.description} />}
          {/* File upload would go here — needs topic context from parent */}
        </div>
      </Collapse>
    </div>
  );
}

/**
 * The core compact question row: label + inline input
 */
function CompactQuestionRow({
  question,
  isComplete,
  isNew,
  onUpdateQuestion,
  jwt,
  prefillValue,
  applicationId,
}: {
  question: IQuestion;
  isComplete: boolean;
  isNew?: boolean;
  onUpdateQuestion: UpdateQuestionCallback;
  jwt?: string;
  prefillValue?: AllowedValueTypes;
  applicationId?: string;
}) {
  const [localValue, setLocalValue] = useState<AllowedValueTypes>(question.value ?? prefillValue ?? '');
  const [saving, setSaving] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [tagAction] = useTags();
  const { enqueueSnackbar } = useSnackbar();

  // Sync local value when question value changes from external update (tree refresh)
  useEffect(() => {
    if (question.value !== undefined && question.value !== null) {
      setLocalValue(question.value);
    }
  }, [question.value]);

  // Pre-fill from SEMIAUTO user values when they arrive after initial render
  useEffect(() => {
    if (prefillValue !== undefined && !isComplete && (question.value === undefined || question.value === null)) {
      setLocalValue(prefillValue);
    }
  }, [prefillValue]);

  const handleChange = useCallback(
    (value: AllowedValueTypes) => {
      setLocalValue(value);

      setSaving(true);
      // Debounce the actual mutation for non-text inputs (currency, etc.)
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        tagAction(question.tags, value)
          .then((proceed) => {
            if (!proceed) {
              setSaving(false);
              return;
            }
            return onUpdateQuestion(question._id, value, false, jwt);
          })
          .then(() => setSaving(false))
          .catch((e) => {
            setSaving(false);
            enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
          });
      }, 300);
    },
    [question._id, question.tags, onUpdateQuestion, jwt, tagAction, enqueueSnackbar]
  );

  // Commit text value immediately — called on Enter key
  const handleCommitText = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSaving(true);
    tagAction(question.tags, localValue)
      .then((proceed) => {
        if (!proceed) {
          setSaving(false);
          return;
        }
        return onUpdateQuestion(question._id, localValue, true, jwt);
      })
      .then(() => setSaving(false))
      .catch((e) => {
        setSaving(false);
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  }, [question._id, question.tags, localValue, onUpdateQuestion, jwt, tagAction, enqueueSnackbar]);

  // For select-one (choice), we want immediate updates
  const handleSelectOne = useCallback(
    (value: string) => {
      setLocalValue(value);
      setSaving(true);
      tagAction(question.tags, value)
        .then((proceed) => {
          if (!proceed) {
            setSaving(false);
            return;
          }
          return onUpdateQuestion(question._id, value, true, jwt);
        })
        .then(() => setSaving(false))
        .catch((e) => {
          setSaving(false);
          enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
        });
    },
    [question._id, question.tags, onUpdateQuestion, jwt, tagAction, enqueueSnackbar]
  );

  const hasDescription = !!question.description;
  const isTooltip = question.tags.process.includes('TOOLTIP');

  return (
    <div
      className={cx('flex flex-col gap-2 p-3 rounded-md border transition-all duration-300', {
        'border-green-200 bg-green-50/50': isComplete && !isNew,
        'border-blue-300 bg-blue-50/50 ring-1 ring-blue-200': isNew,
        'border-slate-200 bg-white': !isComplete && !isNew,
      })}
    >
      {/* Question text row */}
      <div className="flex items-start gap-2">
        <div className="mt-0.5 shrink-0">
          {isComplete ? (
            <CheckCircleOutlineIcon className="text-green-500" fontSize="small" />
          ) : (
            <RadioButtonUncheckedIcon className="text-slate-300" fontSize="small" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <Markdown
              className="text-sm font-medium text-slate-800 [&_p]:m-0 [&_p]:leading-snug flex-1"
              text={question.questionText}
            />
            {hasDescription && (
              <button
                onClick={() => setShowDescription(!showDescription)}
                className={cx(
                  'shrink-0 transition-colors',
                  showDescription ? 'text-primary-root' : 'text-slate-300 hover:text-slate-500'
                )}
                title="Info"
              >
                <InfoOutlinedIcon style={{ fontSize: 18 }} />
              </button>
            )}
            {saving && <span className="text-xs text-slate-400 shrink-0 animate-pulse">...</span>}
          </div>
          {hasDescription && (
            <Collapse in={showDescription || (!isTooltip && !isComplete)}>
              <Markdown className="text-xs text-slate-500 mt-1 [&_p]:m-0" text={question.description} />
            </Collapse>
          )}
        </div>
      </div>

      {/* Input row */}
      <div className="ml-7">
        <CompactInput
          question={question}
          value={localValue}
          onChange={handleChange}
          onSelectOne={handleSelectOne}
          onConfirmText={handleCommitText}
          applicationId={applicationId}
        />
      </div>
    </div>
  );
}

/**
 * Routes to the correct compact input component based on question type.
 */
function CompactInput({
  question,
  value,
  onChange,
  onSelectOne,
  onConfirmText,
  applicationId,
}: {
  question: IQuestion;
  value: AllowedValueTypes;
  onChange: (value: AllowedValueTypes) => void;
  onSelectOne: (value: string) => void;
  onConfirmText: () => void;
  applicationId?: string;
}) {
  switch (question.type) {
    case QuestionType.SELECT_ONE:
      return <CompactRadioInput options={question.options} value={value as string} onChange={onSelectOne} />;
    case QuestionType.SELECT_SOME: {
      const category = question.tags?.variable;
      // Service-selection questions in the DSE app source their options from the
      // snippet catalogue and surface the website-scan prefill banner.
      if (applicationId === SERVICE_SELECTION_APP_ID && isSnippetCategoryKey(category)) {
        return (
          <CompactServiceSelect category={category} value={(value as string[]) || []} onChange={(v) => onChange(v)} />
        );
      }
      return <CompactSelectSome options={question.options} value={(value as string[]) || []} onChange={onChange} />;
    }
    case QuestionType.TEXT:
      return <CompactTextInput value={(value as string) || ''} onChange={onChange} onConfirm={onConfirmText} />;
    case QuestionType.MULTILINE_TEXT:
      return <CompactMultilineInput value={(value as string) || ''} onChange={onChange} onConfirm={onConfirmText} />;
    case QuestionType.CURRENCY:
      return <CompactCurrencyInput value={value as number} onChange={onChange} unit="€" />;
    case QuestionType.FLOAT:
      return <CompactCurrencyInput value={value as number} onChange={onChange} unit="" />;
    case QuestionType.DATE:
      return <CompactDateInput value={value as ISimpleDateObject} onChange={onChange} />;
    default:
      return <CompactTextInput value={String(value || '')} onChange={onChange} onConfirm={onConfirmText} />;
  }
}

/* ─── Individual compact input components ─── */

function CompactRadioInput({
  options,
  value,
  onChange,
}: {
  options: ISelectOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  // For >5 options, use a dropdown
  if (options.length > 5) {
    return (
      <select
        className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white w-full max-w-md focus:outline-none focus:ring-1 focus:ring-primary-root focus:border-primary-root"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— Bitte wählen —</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={cx(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm cursor-pointer transition-colors',
            {
              'border-primary-root bg-primary-root/10 text-primary-root font-medium': value === opt.value,
              'border-slate-200 bg-white hover:bg-slate-50 text-slate-700': value !== opt.value,
            }
          )}
        >
          <input type="radio" className="sr-only" checked={value === opt.value} onChange={() => onChange(opt.value)} />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

function CompactSelectSome({
  options,
  value,
  onChange,
}: {
  options: ISelectOption[];
  value: string[];
  onChange: (value: AllowedValueTypes) => void;
}) {
  const handleToggle = (optValue: string, checked: boolean) => {
    const newValue = checked ? [...value, optValue] : value.filter((v) => v !== optValue);
    onChange(newValue);
  };

  if (options.length > 9) {
    const selectedOptions = options.filter((opt) => value.includes(opt.value));
    return (
      <Autocomplete
        multiple
        options={options}
        value={selectedOptions}
        onChange={(_, val) => onChange(val.map((o: ISelectOption) => o.value))}
        size="small"
      />
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={cx(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm cursor-pointer transition-colors',
            {
              'border-primary-root bg-primary-root/10 text-primary-root': value.includes(opt.value),
              'border-slate-200 bg-white hover:bg-slate-50 text-slate-700': !value.includes(opt.value),
            }
          )}
        >
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded border-slate-300"
            checked={value.includes(opt.value)}
            onChange={(e) => handleToggle(opt.value, e.target.checked)}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

function CompactTextInput({
  value,
  onChange,
  onConfirm,
}: {
  value: string;
  onChange: (value: AllowedValueTypes) => void;
  onConfirm: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5 max-w-md">
      <input
        type="text"
        className="text-sm border border-slate-300 rounded px-2 py-1.5 w-full focus:outline-none focus:ring-1 focus:ring-primary-root focus:border-primary-root"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onConfirm();
          }
        }}
      />
    </div>
  );
}

function CompactMultilineInput({
  value,
  onChange,
  onConfirm,
}: {
  value: string;
  onChange: (value: AllowedValueTypes) => void;
  onConfirm: () => void;
}) {
  return (
    <div className="flex items-start gap-1.5 max-w-md">
      <textarea
        className="text-sm border border-slate-300 rounded px-2 py-1.5 w-full resize-y focus:outline-none focus:ring-1 focus:ring-primary-root focus:border-primary-root"
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onConfirm();
          }
        }}
      />
    </div>
  );
}

function CompactCurrencyInput({
  value,
  onChange,
  unit,
}: {
  value: number;
  onChange: (value: AllowedValueTypes) => void;
  unit: string;
}) {
  return (
    <div className="flex items-center gap-1 max-w-xs">
      <input
        type="number"
        className="text-sm border border-slate-300 rounded px-2 py-1.5 w-full focus:outline-none focus:ring-1 focus:ring-primary-root focus:border-primary-root"
        value={value || ''}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
      {unit && <span className="text-sm text-slate-500">{unit}</span>}
    </div>
  );
}

function CompactDateInput({
  value,
  onChange,
}: {
  value: ISimpleDateObject;
  onChange: (value: AllowedValueTypes) => void;
}) {
  const selected = useMemo(() => {
    if (value && typeof value === 'object' && 'year' in value) {
      const d = new Date();
      d.setUTCFullYear(value.year, value.month, value.day);
      return d;
    }
    return new Date();
  }, [value]);

  const handleChange = (date: Date | null) => {
    if (date && date instanceof Date) {
      onChange({
        day: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear(),
      });
    }
  };

  // Auto-set today if no value
  useEffect(() => {
    if (!value) {
      handleChange(new Date());
    }
  }, []);

  return (
    <DatePicker
      className="text-sm border border-slate-300 rounded px-2 py-1.5 max-w-xs focus:outline-none focus:ring-1 focus:ring-primary-root focus:border-primary-root"
      dateFormat="dd.MM.yyyy"
      selected={selected}
      onChange={handleChange}
    />
  );
}
